<template>
  <v-card class="py-2 mx-auto" min-width="100" :max-width="MAX_CONTENT_WIDTH">
    <h1 class="page-title">{{ pageHeader }}</h1>
    <v-divider></v-divider>
    <div class="d-flex justify-end">
      <div class="filter-wrapper">
        <select-dense
          v-model="sortBy"
          :items="sortOptions"
          item-text="label"
          item-value="value"
          @change="resetWorks"
          placeholder="sort"
        ></select-dense>
      </div>
      <div class="filter-wrapper"
        ><select-dense
          v-model="filter"
          :items="mediumFilters"
          item-text="label"
          item-value="value"
          @change="resetWorks"
        ></select-dense>
      </div>
    </div>
    <v-divider></v-divider>
    <div v-if="!resultsLoading && userWorkRelationshipResults.totalCount == 0" class="no-items-found-message">
      No works found
    </div>
    <page-selection v-if="totalPages > 1" :total-pages="totalPages" v-model="page"></page-selection>
    <div v-if="resultsLoading" class="text-center py-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <div v-else-if="userWorkRelationshipResults.totalCount > 0" class="user-work-relationship-entries justify-center">
      <div v-for="item in userWorkRelationshipResults.items" :key="`${item.work.id}-${item.work.medium}`" class="entry">
        <div class="blurred-poster-wrapper">
          <posterBlurredMargins
            :src="item.work.posterSource"
            :to="{ name: 'work', params: { medium: item.work.medium, workId: item.work.id } }"
            rounded
            :aspect="getAspectRatio(item.work.medium)"
            :max-height="150"
            vertical
            shadow
          >
            <medium-icon :medium="item.work.medium" :size="24" class="ml-1 mt-1"></medium-icon>

            <v-menu v-if="authenticatedUser" offset-y transition="slide-y-transition">
              <template #activator="{ on, attrs }">
                <button v-bind="attrs" v-on="on" class="utility">
                  <v-icon dark>mdi-dots-horizontal</v-icon>
                </button>
              </template>
              <v-list class="utility-menu user-work-relationship-action-menu" dense>
                <v-list-item @click="toggleEngaged(item)">
                  <template v-if="item.authenticatedUserInfo.hasEngaged">
                    <v-icon class="mr-1">mdi-eye-off</v-icon>
                    <v-list-item-content>
                      <!--TODO: Consider if we should distinguish between "Remove from" for list owner an "Previously engaged" for non-owner-->
                      Remove from {{ mediumSpecificVerbForEngaged(item.work.medium).toLowerCase() }}
                    </v-list-item-content>
                  </template>
                  <template v-else>
                    <v-icon class="mr-1">mdi-eye</v-icon>
                    <v-list-item-content>
                      Mark as {{ mediumSpecificVerbForEngaged(item.work.medium).toLowerCase() }}
                    </v-list-item-content>
                  </template>
                </v-list-item>
                <v-list-item @click="toggleSavedForLater(item)">
                  <template v-if="item.authenticatedUserInfo.savedForLater">
                    <v-icon class="mr-1">mdi-bookmark-off</v-icon>
                    <v-list-item-content> Remove from saved for later </v-list-item-content>
                  </template>
                  <template v-else>
                    <v-icon class="mr-1">mdi-bookmark</v-icon>
                    <v-list-item-content>Save for later</v-list-item-content>
                  </template>
                </v-list-item>
                <v-list-item :to="{ name: 'rate', query: { medium: item.work.medium, workID: item.work.id } }">
                  <v-icon class="mr-1">mdi-file-document-edit</v-icon>
                  <v-list-item-content>New review</v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-icon class="mr-1">mdi-close</v-icon>
                  <v-list-item-content>Cancel</v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </posterBlurredMargins>
        </div>

        <router-link :to="{ name: 'work', params: { medium: item.work.medium, workId: item.work.id } }">
          <p class="work-title">
            {{ item.work.title }}
          </p>
        </router-link>
        <p class="subtext">{{ subtext(item.work) }}</p>
      </div>
    </div>
    <page-selection v-if="totalPages > 1" :total-pages="totalPages" v-model="page"></page-selection>
  </v-card>
</template>

<script>
import { mapMutations } from "vuex";
// Util
import getAspectRatio from "../util/getAspectRatio.js";
import { MAX_CONTENT_WIDTH, USER_WORK_RELATIONSHIPS } from "../util/constants.js";
import { formatDate } from "../util/dateHelpers.js";
import { mediumIsValid, mediumSpecificVerbForEngaged, userFriendlyMediumWord } from "../util/mediumHelpers.js";
import { mediums } from "../util/enums.js";
// Components
import posterBlurredMargins from "../components/poster-blurred-margins.vue";
import mediumIcon from "../components/medium-icon.vue";
import pageSelection from "../components/page-selection.vue";
import SelectDense from "../components/select-dense.vue";
// Mixins
import WatchlistAndSeen from "@/mixins/watchlistAndSeen";
// Types
// eslint-disable-next-line no-unused-vars
import * as StoreTypes from "../types/store.js";

const mediumFilters = [];
for (const medium of Object.values(mediums)) {
  mediumFilters.push({
    label: userFriendlyMediumWord(medium),
    value: medium,
  });
}

/**
 * @typedef {Object} UserWorkRelationshipConfig
 * @property {String} pageHeader
 * @property {String} removeIcon
 * @property {String} getEndpoint
 */

/** @type {UserWorkRelationshipConfig} */
const SAVED_FOR_LATER_CONFIG = {
  pageHeader: "Saved for Later",
  removeIcon: "mdi-bookmark-off",
  getEndpoint: "/v1/saved-for-later",
};

/** @type {UserWorkRelationshipConfig} */
const ENGAGED_CONFIG = {
  pageHeader: "Works Engaged",
  removeIcon: "mdi-eye-off",
  getEndpoint: "/v1/work-engagements",
};

export default {
  name: "user-work-relationship-page-content",

  components: { posterBlurredMargins, mediumIcon, pageSelection, SelectDense },
  mixins: [WatchlistAndSeen],

  props: {
    // TODO: Add validation
    relationship: {
      type: String,
      default: USER_WORK_RELATIONSHIPS.SAVED_FOR_LATER,
    },
  },

  data: () => ({
    MAX_CONTENT_WIDTH,
    mediumFilters,

    sortOptions: [
      {
        label: "Time Added (Oldest First)",
        value: {
          field: "timeAdded",
          order: "ASCENDING",
        },
      },
      {
        label: "Time Added (Newest First)",
        value: {
          field: "timeAdded",
          order: "DESCENDING",
        },
      },
      {
        label: "Title",
        value: {
          field: "title",
          order: "ASCENDING",
        },
      },
    ],
    sortBy: null,

    userId: null,

    filter: null,

    ITEMS_PER_PAGE: 50,
    page: 1,

    resultsLoading: true,
    userWorkRelationshipResults: {
      totalCount: null,
      items: [],
    },

    COLUMN_WIDTH: 100,
  }),

  computed: {
    /** @returns {UserWorkRelationshipConfig} */
    relationshipConfig() {
      switch (this.relationship) {
        case USER_WORK_RELATIONSHIPS.SAVED_FOR_LATER:
          return SAVED_FOR_LATER_CONFIG;
        case USER_WORK_RELATIONSHIPS.ENGAGED:
          return ENGAGED_CONFIG;
        default:
          throw new Error("Relationship not recognized");
      }
    },

    totalPages() {
      return this.userWorkRelationshipResults.totalCount
        ? Math.ceil(this.userWorkRelationshipResults.totalCount / this.ITEMS_PER_PAGE)
        : 1;
    },

    pageHeader() {
      return `${this.authenticatedUserIsRouteUser ? "My" : this.routeUser + "'s"} ${this.relationshipConfig.pageHeader}`;
    },

    removeIcon() {
      return this.relationshipConfig.removeIcon;
    },

    authenticatedUser() {
      return this.$store.state.username;
    },

    routeUser() {
      return this.$route.params.username;
    },

    routeMedium() {
      return this.$route.query.medium;
    },

    authenticatedUserIsRouteUser() {
      return this.authenticatedUser && this.routeUser.toLowerCase() == this.authenticatedUser.toLowerCase();
    },
  },

  async mounted() {
    // Remove ?medium= parameter from route if it has an invalid value
    if (this.routeMedium && !mediumIsValid(this.routeMedium)) {
      const routeQuery = Object.assign({}, this.$route.query);
      delete routeQuery.medium;
      this.$router.replace({ query: routeQuery });
    }

    // Load saved state for easier backwards navigation
    /** @type {StoreTypes.UserWorkRelationshipPageState} */
    const pageState = this.$store.state.userWorkRelationshipPages[this.relationship];
    const loadFromState =
      pageState.routeUsername &&
      pageState.routeUsername === this.routeUser &&
      (!this.routeMedium || this.routeMedium == pageState.filter);
    if (loadFromState) {
      this.page = pageState.page;
      this.filter = pageState.filter;
      this.sortBy = pageState.sortBy;
      this.userWorkRelationshipResults = pageState.results;
      await this.$vuetify.goTo(pageState.scrollY, {
        duration: 0,
      });
      this.resultsLoading = false;
    }

    const response = await this.$axios.get("/profile", {
      params: {
        user: this.routeUser,
      },
    });
    this.userId = response.data.id;
    if (!loadFromState && this.routeMedium) {
      this.filter = this.routeMedium;
    }
    await this.loadWorks();
  },

  watch: {
    page() {
      this.loadWorks();
    },
  },

  methods: {
    ...mapMutations(["showSnackbar", "saveUserWorkRelationshipPage"]),
    getAspectRatio,
    mediumSpecificVerbForEngaged,

    loadWorks: async function () {
      this.resultsLoading = true;
      const response = await this.$axios.get(this.relationshipConfig.getEndpoint, {
        params: {
          userId: this.userId,
          offset: this.ITEMS_PER_PAGE * (this.page - 1),
          limit: this.ITEMS_PER_PAGE,
          medium: this.filter ? this.filter : undefined,
          sortBy: this.sortBy && this.sortBy.field ? this.sortBy.field : "timeAdded",
          order: this.sortBy && this.sortBy.order ? this.sortBy.order : "ASCENDING",
        },
      });
      this.userWorkRelationshipResults.totalCount = response.data.totalCount;
      this.userWorkRelationshipResults.items = response.data.items;
      this.resultsLoading = false;
    },

    subtext(work) {
      switch (work.medium) {
        case this.$mediums.FILM:
        case this.$mediums.TV_SHOW:
          return work.releaseDate ? formatDate(work.releaseDate) : null;
        case this.$mediums.PODCAST:
        case this.$mediums.ALBUM:
        case this.$mediums.BOOK:
          return work.creators ? work.creators[0].name : null;
        default:
          return null;
      }
    },

    // TODO: Combine with toggleSavedForLater into 1 function using polymorphism
    // TODO: Add type to parameter
    async toggleEngaged(item) {
      try {
        if (item.authenticatedUserInfo.hasEngaged) {
          if (this.relationship == USER_WORK_RELATIONSHIPS.ENGAGED && this.authenticatedUser === this.routeUser) {
            await this.removeRelationship(item.work.id, item.work.medium);
          } else {
            await this.deleteFromViewed(item.work.id, item.work.medium, false);
          }
          this.showSnackbar({
            text: "Successfully removed from " + mediumSpecificVerbForEngaged(item.work.medium).toLowerCase(),
          });
        } else {
          await this.markAsViewed(item.work.id, item.work.medium);
          this.showSnackbar({
            text: "Successfully marked as " + mediumSpecificVerbForEngaged(item.work.medium).toLowerCase(),
          });
        }
        item.authenticatedUserInfo.hasEngaged = !item.authenticatedUserInfo.hasEngaged;
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: "An unexpected error has occurred", error: true });
      }
    },

    async toggleSavedForLater(item) {
      try {
        if (item.authenticatedUserInfo.savedForLater) {
          if (
            this.relationship == USER_WORK_RELATIONSHIPS.SAVED_FOR_LATER &&
            this.authenticatedUser === this.routeUser
          ) {
            await this.removeRelationship(item.work.id, item.work.medium);
          } else {
            await this.removeFromWatchlist(item.work.id, item.work.medium, false);
          }
          this.showSnackbar({
            text: "Successfully removed from saved for later",
          });
        } else {
          await this.saveForLater(item.work.id, item.work.medium);
          this.showSnackbar({
            text: "Successfully saved for later",
          });
        }
        item.authenticatedUserInfo.savedForLater = !item.authenticatedUserInfo.savedForLater;
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: "An unexpected error has occurred", error: true });
      }
    },

    async removeRelationship(workId, medium) {
      // TODO: Factor out this if chain
      if (this.relationship === USER_WORK_RELATIONSHIPS.SAVED_FOR_LATER) {
        await this.removeFromWatchlist(workId, medium, false);
      } else if (this.relationship === USER_WORK_RELATIONSHIPS.ENGAGED) {
        await this.deleteFromViewed(workId, medium);
      } else {
        throw new Error("Unexpected relationship");
      }
      const removedItemIdx = this.userWorkRelationshipResults.items.findIndex((item) => item.work.id === workId);
      this.userWorkRelationshipResults.items.splice(removedItemIdx, 1);

      if (this.page < this.totalPages) {
        // Minus 1 to account for the item that was just removed
        const replacementItemIdx = this.page * this.ITEMS_PER_PAGE - 1;
        const replacementItemResponse = await this.$axios.get(this.relationshipConfig.getEndpoint, {
          params: {
            userId: this.userId,
            offset: replacementItemIdx,
            limit: 1,
          },
        });
        this.userWorkRelationshipResults.totalCount = replacementItemResponse.data.totalCount;
        this.userWorkRelationshipResults.items = this.userWorkRelationshipResults.items.concat(
          replacementItemResponse.data.items,
        );
      } else {
        // This needs to be updated after the check for page in case there is only 1 item on the following page.
        this.userWorkRelationshipResults.totalCount -= 1;
      }
    },

    async resetWorks() {
      this.page = 1;
      this.userWorkRelationshipResults.items = [];
      this.userWorkRelationshipResults.totalCount = null;

      await this.loadWorks();
    },

    // We can't use beforeRouteLeave because this is a child component and not directly a Vue-router route
    prepareRouteLeave() {
      this.saveUserWorkRelationshipPage({
        interactionType: this.relationship,
        pageState: {
          page: this.page,
          routeUsername: this.routeUser,
          filter: this.filter,
          sortBy: this.sortBy
            ? {
                field: this.sortBy.field,
                order: this.sortBy.order,
              }
            : {
                field: null,
                order: null,
              },
          results: this.userWorkRelationshipResults,
          scrollY: document.documentElement.scrollTop,
        },
      });
    },
  },
};
</script>

<style scoped>
.user-work-relationship-entries {
  margin: 8px;
  display: grid;
  grid-template-columns: repeat(auto-fill, 100px);
  gap: 16px;
  justify-items: center;
}
.user-work-relationship-entries .entry {
  width: 100px;
}
.user-work-relationship-entries .entry .blurred-poster-wrapper {
  height: 150px;
}
.user-work-relationship-entries .entry .blurred-poster-wrapper button.utility {
  position: absolute;
  top: 4px;
  right: 4px;

  height: 24px;
  width: 24px;

  display: block;
  border-radius: 100%;
  background-color: var(--semi-opaque-overlay-bg-color);
}
.user-work-relationship-entries .entry .blurred-poster-wrapper button.utility .v-icon {
  vertical-align: baseline;
}
.user-work-relationship-entries .entry p {
  margin-bottom: 0;
  font-size: 12px;
  line-height: 14px;

  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* number of lines to show */
  line-clamp: 2;
}
.user-work-relationship-entries .entry p.work-title {
  font-weight: bold;
  color: var(--v-text-base);
  margin-top: 4px;
}
.user-work-relationship-entries .entry .subtext {
  font-weight: normal;
  color: var(--v-text-lighten1);
}
</style>
